import {
  IncidentMessage,
  IncidentMessageType,
  lightTheme,
  vDriveDeliveryStatus,
} from '@vgrubs/components';

// Barrel values (enums, the theme) are only read inside these functions: this page is imported
// eagerly by the route table, and a top-level read can run before the barrel has initialised.

/** The statuses the backend accepts a message for, in the order a delivery reaches them. */
export function messageStatuses() {
  return [
    vDriveDeliveryStatus.pending_assign,
    vDriveDeliveryStatus.runner_assigned,
    vDriveDeliveryStatus.arrived_pickup,
    vDriveDeliveryStatus.en_route_dropoff,
    vDriveDeliveryStatus.completed,
    vDriveDeliveryStatus.cancelled,
  ];
}

/** Each status takes one message. `keep` stays available: it is the one being edited. */
export function freeStatuses(
  messages: IncidentMessage[],
  keep?: vDriveDeliveryStatus | null
) {
  return messageStatuses().filter(
    (status) => status === keep || !messages.some((m) => m.status === status)
  );
}

export function isEtaMessage(message: IncidentMessage) {
  return message.type === IncidentMessageType.etaIncreased;
}

export function isFoodRatingMessage(message: IncidentMessage) {
  return message.type === IncidentMessageType.foodRating;
}

/** The types that take one message and no status. */
export function isSingleType(type: IncidentMessageType) {
  return (
    type === IncidentMessageType.etaIncreased ||
    type === IncidentMessageType.foodRating
  );
}

/** Status messages in delivery order, then the running-late one, then food rating. */
export function sortMessages(messages: IncidentMessage[]) {
  // The enum is declared in delivery order.
  const order = Object.values(vDriveDeliveryStatus);
  const rank = (m: IncidentMessage) =>
    m.status
      ? order.indexOf(m.status)
      : order.length + (isFoodRatingMessage(m) ? 1 : 0);
  return [...messages].sort((a, b) => rank(a) - rank(b));
}

export const ETA_TAG = '#eta';
export const REVIEW_TAG = '#review-link';

/**
 * The backend rejects `#eta` anywhere but the running-late message, and `#review-link` (the
 * order review page) anywhere but the food-rating one.
 */
export function tagsFor(type: IncidentMessageType) {
  const tags = ['#customer-name', '#store-name', '#tracking-link'];
  if (type === IncidentMessageType.etaIncreased) return [...tags, ETA_TAG];
  if (type === IncidentMessageType.foodRating) return [...tags, REVIEW_TAG];
  return tags;
}

/** A tag in `message` that `type` does not take, to stop before the backend's 400. */
export function misplacedTag(type: IncidentMessageType, message: string) {
  return [ETA_TAG, REVIEW_TAG].find(
    (tag) => message.includes(tag) && !tagsFor(type).includes(tag)
  );
}

export function typeOptions() {
  return [
    {
      type: IncidentMessageType.status,
      label: 'Status change',
      description:
        'Sent to the customer when the delivery reaches the selected status.',
    },
    {
      type: IncidentMessageType.etaIncreased,
      label: 'Delivery running late',
      description:
        'Sent to the customer when their estimated arrival time moves later, at most once every 10 minutes.',
    },
    {
      type: IncidentMessageType.foodRating,
      label: 'Food rating',
      description:
        'Sent to the customer 15 minutes after a vDrive delivery, once per order, asking them to rate the food. Skipped if they already rated it.',
    },
  ];
}

export function runningLatePill() {
  // Blue: no status pill uses it, and orange sat too close to Pending Assign's yellow.
  return { name: 'Running Late', color: lightTheme.blue };
}

export function foodRatingPill() {
  return { name: 'Food Rating', color: lightTheme.darkBlue };
}
